import { useMemo } from 'react';

import { SITE_MANAGER, WORK_ORDER_COLLABORATOR } from 'utils/access-control/roles';
import { usePermissions } from 'utils/usePermissions';

import type { DashboardLayoutState } from '../types';

import { type PresetId, getPresetLayout } from './presetCatalog';

/**
 * Resolves a persona preset from the user's role. Single-role-per-org, so this is
 * a simple precedence: WO collaborator → fleet manager (site manager) → general
 * (editor / read-only / fallback). Never binding — the user can customize freely.
 */
export function resolvePresetId(hasRole: (role: string) => boolean): PresetId {
  if (hasRole(WORK_ORDER_COLLABORATOR)) {
    return 'work-order-collaborator';
  }
  if (hasRole(SITE_MANAGER)) {
    return 'fleet-manager';
  }
  return 'general';
}

export interface PersonaPreset {
  presetId: PresetId;
  layout: DashboardLayoutState;
}

export function usePersonaPreset(): PersonaPreset {
  const { hasRole } = usePermissions();
  const presetId = resolvePresetId(hasRole);
  const layout = useMemo(() => getPresetLayout(presetId), [presetId]);
  return { presetId, layout };
}
